import { useState, useEffect, useMemo } from "react";
import { useUser } from "@/hooks/use-user";
import { useLocation, useRoute, useSearch } from "wouter";
import Layout from "@/components/layout";
import useSWR, { mutate } from "swr";
import type { Candidate } from "db/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Box, Button } from "@mui/material";
import { MapPin, LinkedinIcon, Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "../../supabaseClient";
import { useToast } from "@/hooks/use-toast";
import { CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fetchLinkedinCandidates } from "@/services/linkedin";





const fetchCandidates = async () => {
  const { data, error } = await supabase.from("candidates").select("*").eq('status', 'active');
  if (error) {
    console.error("Error fetching candidates:", error);
    throw error;
  }
  return data;
};

const SearchCandidates = () => {
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const queryParams = new URLSearchParams(searchString);
  const { user, isLoading } = useUser();
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [match, params] = useRoute("/search-candidates/:roleId");
  const roleId = params?.roleId;
  const [isLoadingState, setIsLoadingState] = useState<Record<string, boolean>>({});
  const { data: candidates, error } = useSWR("/api/candidates", fetchCandidates);
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    location: "",
    experience: "",
    status: "",
    skills: "",
  });
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [linkedInCandidates, setLinkedInCandidates] = useState([]);
  const [isLoadingLinkedIn, setIsLoadingLinkedIn] = useState(false);

  const { toast } = useToast();
  const [clickedCandidates, setClickedCandidates] = useState<Set<string>>(new Set());
  const [activetab, setActiveTab] = useState('internal');

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  const handleFilterChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, 300);

  const handleTabChange = (value) => {
    setActiveTab(value);
  };


  useEffect(() => {
    if (activetab === "linkedin" && !isLoadingLinkedIn) {

      const queryParamsMap = new Map<string, string>();
      queryParams.forEach((value, key) => {
        queryParamsMap.set(key, value);
      });
      queryParamsMap.set('open_to_work','open to work');
      fetchLinkedinCandidates(queryParamsMap).then(data => {
        setLinkedInCandidates(data);
        setIsLoadingLinkedIn(true);
      });
    }
  }, [activetab, isLoadingLinkedIn]);

  const filteredCandidates = useMemo(
    () =>
      candidates?.filter((candidate) => {
        const isAdded = clickedCandidates.has(candidate.id);
        const candidateLocation = candidate.location || "";
        const filterLocation = filters.location || "";
        const candidateName = candidate.name?.toLowerCase() || "";
        const candidateEmail = candidate.email?.toLowerCase() || "";
        const candidateStatus = candidate.status?.toLowerCase() || "";
        const candidateSkills = (candidate.skills?.length === 0) ? [] : candidate.skills;

        return (
          !isAdded &&
          (filters.name === "" || candidateName.includes(filters.name.toLowerCase())) &&
          (filters.email === "" || candidateEmail.includes(filters.email.toLowerCase())) &&
          (filterLocation === "" || candidateLocation.toLowerCase().includes(filterLocation.toLowerCase())) &&
          (filters.experience === "" || candidate.experience >= parseInt(filters.experience, 10)) &&
          (filters.status === "" || candidateStatus.includes(filters.status.toLowerCase())) &&
          (filters.skills === "" || (candidateSkills ? candidateSkills.map((skill: string) => skill.toLowerCase()).some((skill: string) => skill.includes(filters.skills.toLowerCase())) : false)) &&
          (candidate.source.toLowerCase() === activetab) &&
          (candidate.source === "internal")
        );
      }) || [],
    [candidates, filters, clickedCandidates, activetab]
  );


  const handleAddToRole = async (candidate, roleId: string) => {
    let candidateId = candidate.id;
    if (clickedCandidates.has(candidateId)) return;

    try {


      if (activetab === "linkedin" && !candidateId) {
        console.log("Checking if LinkedIn candidate already exists in the database");
        const { data: existingCandidate, error: fetchError } = await supabase
          .from("candidates")
          .select("id")
          .eq("linkedInUrl", candidate.linkedInUrl)
          .single();

        if (fetchError && fetchError.code !== "PGRST116") {
          throw fetchError;
        }
        console.log(existingCandidate);
        if (existingCandidate) {
          candidateId = existingCandidate.id;
        } else {
          console.log("Adding LinkedIn candidate to candidates database");
          const { data, error } = await supabase
            .from("candidates")
            .insert({
              name: candidate.name,
              source: "linkedin",
              linkedInUrl: candidate.linkedInUrl
            })
            .select("id")
            .single();
          console.log(data);
          if (error) {
            throw error;
          }
          candidateId = data.id;
        }
      }
      setIsLoadingState(prev => ({ ...prev, [candidateId]: true }));
      setClickedCandidates(prev => new Set(prev).add(candidateId));
      const { error } = await supabase
        .from("project_candidate")
        .insert({
          project_id: roleId,
          candidate_id: candidateId,
          data_status: "added",
        })
        .single();

      if (error) {
        setClickedCandidates(prev => {
          const newSet = new Set(prev);
          newSet.delete(candidateId);
          return newSet;
        });

        if (error.code === "23505") {
          toast({
            title: "Error: Already added to the role", status: "info", duration: 1000,
            isClosable: true
          });
        } else {
          toast({
            title: "Failed to add", status: "error", duration: 1000,
            isClosable: true
          });
        }
      } else {
        toast({
          title: "Successfully added", status: "success", duration: 1000,
          isClosable: true
        });
        mutate("/api/candidates");
      }
    } catch (err) {
      toast({
        title: "Error occurred", status: "error", duration: 1000,
        isClosable: true
      });
    } finally {
      setIsLoadingState(prev => ({ ...prev, [candidateId]: false }));
    }

  };



  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold tracking-tight">Search Candidates</h2>
          <Box>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{
                backgroundColor: "black",
                color: "white",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </div>

        {/* Filters */}
        <div>
          <button
            onClick={() => setFiltersVisible(!filtersVisible)}
            className="mb-4 px-4 py-2 bg-black text-white rounded"
          >
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </button>
          {filtersVisible && (
            <div className="space-y-4 p-4 border rounded bg-gray-100">
              <div className="flex flex-wrap gap-4">
                {["name", "email", "location", "experience", "status", "skills"].map((filter) => (
                  <div className="flex flex-col" key={filter}>
                    <label className="block text-sm font-medium">{filter}</label>
                    <input
                      type="text"
                      name={filter}
                      onChange={handleFilterChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                ))}
              </div>
            </div>

          )}
        </div>
        <Tabs defaultValue="internal" onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="internal">Internal Candidates</TabsTrigger>
            <TabsTrigger value="linkedin">LinkedIn Candidates</TabsTrigger>
          </TabsList>
          <TabsContent value="internal" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredCandidates.map((candidate, index) => candidate && (
                <Card key={`${candidate.id}-${index}`} className="cursor-pointer">
                  <CardContent className="pt-6">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{candidate.name}</h3>
                        <Badge
                          variant={candidate.status === "new" ? "default" : "secondary"}
                        >
                          {candidate.status}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {candidate.location && (
                          <div className="flex items-center">
                            <MapPin className="mr-2 h-4 w-4" />
                            {candidate.location}
                          </div>
                        )}
                        {candidate.experience && (
                          <div className="flex items-center">
                            <Briefcase className="mr-2 h-4 w-4" />
                            {candidate.experience} years
                          </div>
                        )}
                        {candidate.linkedInUrl && (
                          <div className="flex items-center">
                            <LinkedinIcon className="mr-2 h-4 w-4" />
                            <a
                              href={candidate.linkedInUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              LinkedIn Profile
                            </a>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          marginTop: "8px",
                          backgroundColor: candidate.isAdded ? "#999" : "#000",
                          color: "#fff",
                          "&:hover": { backgroundColor: candidate.isAdded ? "#999" : "#333" },
                        }}
                        onClick={() => handleAddToRole(candidate, roleId!)}
                        disabled={candidate.isAdded || isLoadingState[candidate.id]}
                      >
                        {isLoadingState[candidate.id] ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          candidate.isAdded ? "Added" : "Add to Role"
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="linkedin" className="mt-6">
            {/* Candidate Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {linkedInCandidates.map((candidate, index) => candidate && (
                <Card key={`${candidate.id}-${index}`} className="cursor-pointer">
                  <CardContent className="pt-6">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{candidate.name}</h3>
                        <Badge
                          variant={candidate.status === "new" ? "default" : "secondary"}
                        >
                          {candidate.status}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {candidate.location && (
                          <div className="flex items-center">
                            <MapPin className="mr-2 h-4 w-4" />
                            {candidate.location}
                          </div>
                        )}
                        {candidate.experience && (
                          <div className="flex items-center">
                            <Briefcase className="mr-2 h-4 w-4" />
                            {candidate.experience} years
                          </div>
                        )}
                        {candidate.linkedInUrl && (
                          <div className="flex items-center">
                            <LinkedinIcon className="mr-2 h-4 w-4" />
                            <a
                              href={candidate.linkedInUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              LinkedIn Profile
                            </a>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          marginTop: "8px",
                          backgroundColor: candidate.isAdded ? "#999" : "#000",
                          color: "#fff",
                          "&:hover": { backgroundColor: candidate.isAdded ? "#999" : "#333" },
                        }}
                        onClick={() => handleAddToRole(candidate, roleId!)}
                        disabled={candidate.isAdded || isLoadingState[candidate.id]}
                      >
                        {isLoadingState[candidate.id] ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          candidate.isAdded ? "Added" : "Add to Role"
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

    </Layout>
  );
};

export default SearchCandidates;
